import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import articles from './articles';
import LayoutNoLogin from '../../components/layout/LayoutNoLogin';
import Layout from '../../components/layout/StandardLayout';
import { BookOpenIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import { useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';

const Breadcrumbs = () => (
    <nav className="flex items-center space-x-2 text-sm text-base-content/60">
        <Link to="/wiki" className="flex items-center hover:text-primary">
            <BookOpenIcon className="w-4 h-4 mr-1" />
            <span>SKS-Wiki</span>
        </Link>
    </nav>
);

const ArticleCard = ({ title, description, image, slug }) => (
    <Link
        to={`/wiki/${slug}`}
        className="group block overflow-hidden rounded-xl bg-base-100 border border-base-300 shadow-sm hover:border-primary/20 hover:shadow-md transition-all"
    >
        {image && (
            <div className="aspect-[2/1] w-full overflow-hidden bg-base-200">
                <img
                    src={image}
                    alt={`SKS-Theorie: ${title}`}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
            </div>
        )}
        <div className="p-4">
            <h3 className="text-base font-medium text-base-content group-hover:text-primary">
                {title}
            </h3>
            {description && (
                <p className="mt-1 text-sm line-clamp-3 text-base-content/70">
                    {description}
                </p>
            )}
        </div>
    </Link>
);

const NavigationTabs = ({ onScrollToSection }) => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-12">
        <button
            onClick={() => onScrollToSection('allgemein')}
            className="btn btn-outline btn-primary"
        >
            Allgemeine SKS-Informationen
        </button>
        <button
            onClick={() => onScrollToSection('grundlagen')}
            className="btn btn-outline btn-primary"
        >
            SKS-Prüfungsrelevante Grundlagen
        </button>
    </div>
);

const WikiHome = () => {
    const { isAuthenticated } = useContext(AuthContext);
    const SelectedLayout = isAuthenticated ? Layout : LayoutNoLogin;

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const allgemeineArtikel = articles.filter(article => 
        !['navigation', 'schifffahrtsrecht', 'wetterkunde', 'seemannschaft'].includes(article.slug)
    );

    const grundlagenArtikel = articles.filter(article => 
        ['navigation', 'schifffahrtsrecht', 'wetterkunde', 'seemannschaft'].includes(article.slug)
    );

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const navbarHeight = 64; // Höhe der Navbar
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
            });
        }
    };

    return (
        <SelectedLayout>
            <Helmet>
                {/* Primary Meta Tags */}
                <title>SKS-Wiki - Alles zur SKS-Theorieprüfung | Navigation, Wetterkunde & mehr</title>
                <meta name="title" content="SKS-Wiki - Alles zur SKS-Theorieprüfung | Navigation, Wetterkunde & mehr" />
                <meta name="description" content="Komplettes SKS-Wiki mit allen Themen zur Theorieprüfung: Navigation, Seemannschaft, Schifffahrtsrecht, Wetterkunde und Kartenaufgaben. Kostenlos lernen für den Sportküstenschifferschein." />
                <meta name="keywords" content="SKS Wiki, SKS Theorie, Navigation Grundlagen, Wetterkunde SKS, Schifffahrtsrecht, Seemannschaft, Kartenaufgaben, SKS Vorbereitung" />
                
                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://mein-sks.de/wiki" />
                <meta property="og:title" content="SKS-Wiki - Alles zur SKS-Theorieprüfung" />
                <meta property="og:description" content="Komplettes SKS-Wiki mit allen Themen zur Theorieprüfung: Navigation, Seemannschaft, Schifffahrtsrecht, Wetterkunde und Kartenaufgaben." />
                <meta property="og:image" content="https://mein-sks.de/app_icon.png" />
                <meta property="og:site_name" content="Mein-SKS" />
                
                {/* Twitter */}
                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:url" content="https://mein-sks.de/wiki" />
                <meta property="twitter:title" content="SKS-Wiki - Alles zur SKS-Theorieprüfung" />
                <meta property="twitter:description" content="Komplettes SKS-Wiki mit allen Themen zur Theorieprüfung: Navigation, Seemannschaft, Schifffahrtsrecht, Wetterkunde und Kartenaufgaben." />
                <meta property="twitter:image" content="https://mein-sks.de/app_icon.png" />
                
                {/* Additional SEO */}
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://mein-sks.de/wiki" />
                
                {/* Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "SKS-Wiki",
                        "description": "Komplettes SKS-Wiki mit allen Themen zur Theorieprüfung",
                        "url": "https://mein-sks.de/wiki",
                        "isPartOf": {
                            "@type": "WebSite",
                            "name": "Mein-SKS",
                            "url": "https://mein-sks.de"
                        },
                        "about": {
                            "@type": "Thing",
                            "name": "SKS Theorieprüfung",
                            "description": "Sportküstenschifferschein Theorieprüfung"
                        }
                    })}
                </script>
            </Helmet>

            <div className="min-h-screen bg-base-100">
                <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
                    <Breadcrumbs />
                    
                    <header className="mt-8 mb-12">
                        <h1 className="text-4xl font-bold tracking-tight text-base-content sm:text-5xl">
                            SKS-Wiki: Alles zur Sportküstenschifferschein-Theorie
                        </h1>
                        <p className="mt-4 text-xl text-base-content/70">
                            Umfassendes Wissen zur SKS-Theorieprüfung - Von Grundlagen bis Prüfungstipps
                        </p>
                        <div className="mt-6 flex flex-wrap gap-4">
                            <Link 
                                to="/" 
                                className="btn btn-primary btn-sm"
                            >
                                Zur SKS-Lern-App
                            </Link>
                            <Link 
                                to="/wiki/weg-zum-sks" 
                                className="btn btn-outline btn-sm"
                            >
                                SKS-Einsteigerguide
                            </Link>
                        </div>
                    </header>

                    <NavigationTabs onScrollToSection={scrollToSection} />

                    <section id="allgemein" className="mb-16">
                        <h2 className="text-3xl font-bold mb-4">Allgemeine SKS-Informationen</h2>
                        <div className="bg-base-200 rounded-xl p-6 mb-8">
                            <p className="text-base-content/70">
                                Hier findest du wichtige organisatorische Informationen und Hilfestellungen rund um die SKS-Prüfung. 
                                Diese Artikel geben dir einen Überblick über den Weg zum SKS-Schein und helfen dir bei der optimalen 
                                <Link to="/" className="link link-primary mx-1">SKS-Theorieprüfung Vorbereitung</Link>.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {allgemeineArtikel.map((article) => (
                                <ArticleCard
                                    key={article.slug}
                                    title={article.title}
                                    description={article.description}
                                    image={article.image}
                                    slug={article.slug}
                                />
                            ))}
                        </div>
                    </section>

                    <section id="grundlagen" className="pt-8">
                        <h2 className="text-3xl font-bold mb-4">SKS-Prüfungsrelevante Grundlagen</h2>
                        <div className="bg-base-200 rounded-xl p-6 mb-8">
                            <div className="flex items-start space-x-4">
                                <div className="bg-primary/10 rounded-lg p-3">
                                    <AcademicCapIcon className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <p className="text-base-content/70">
                                        Diese SKS-Grundlagenartikel decken die wichtigsten Prüfungsthemen ab: Navigation, Schifffahrtsrecht, 
                                        Wetterkunde und Seemannschaft. Wir empfehlen, diese Artikel durchzuarbeiten, bevor du mit den 
                                        <Link to="/" className="link link-primary mx-1">SKS-Theoriefragen</Link> 
                                        beginnst, um ein solides Fundament zu schaffen.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {grundlagenArtikel.map((article) => (
                                <ArticleCard
                                    key={article.slug}
                                    title={article.title}
                                    description={article.description}
                                    image={article.image}
                                    slug={article.slug}
                                />
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </SelectedLayout>
    );
};

export default WikiHome; 